import api from './api.service';

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type TradingRentalEstimationType = 'Industrial Trading' | 'Rental Services' | 'Mixed';
export type TradingRentalLineType = 'Trading' | 'Rental';
/** replace = swap lines of the imported type only; append = add after existing lines. */
export type TradingRentalLineImportMode = 'replace' | 'append';

export interface TradingRentalLineImportPreview {
  lines: Array<Partial<TradingRentalEstimationLine>>;
  errors: string[];
  warnings: string[];
}

export interface TradingRentalEstimationOverhead {
  id?: string;
  line_number?: number;
  overhead_type: string;
  description?: string | null;
  remarks?: string | null;
  currency: string;
  exchange_rate?: number;
  original_amount: number;
  amount?: number;
  base_currency_amount?: number;
}

export interface TradingRentalEstimationOverheadAllocation {
  id: string;
  overhead_id: string;
  line_id: string;
  base_cost_amount: number;
  allocation_ratio: number;
  allocated_amount: number;
  allocation_basis: string;
}

export interface TradingRentalEstimationLine {
  id: string;
  estimation_id: string;
  line_number: number;
  line_type: TradingRentalLineType;
  item_id?: string | null;
  item_code?: string | null;
  item_name?: string | null;
  item_description?: string | null;
  item_category?: string | null;
  brand?: string | null;
  specification?: string | null;
  uom?: string | null;
  quantity: number;
  available_stock: number;
  reserved_stock: number;
  balance_required: number;
  unit_cost: number;
  landed_cost: number;
  margin_percentage: number;
  markup_percentage: number;
  selling_unit_price: number;
  discount_percentage: number;
  tax_percentage: number;
  cost_amount: number;
  base_cost_amount?: number;
  allocated_overhead_amount?: number;
  loaded_cost_amount?: number;
  margin_amount: number;
  selling_amount: number;
  tax_amount: number;
  total_amount: number;
  expected_delivery_date?: string | null;
  supplier_preference?: string | null;
  rental_period_type?: string | null;
  rental_start_date?: string | null;
  rental_end_date?: string | null;
  rental_duration: number;
  rental_rate: number;
  security_deposit: number;
  mobilization_charges: number;
  demobilization_charges: number;
  operator_charges: number;
  transport_charges: number;
  damage_charges: number;
  availability_status?: string | null;
  remarks?: string | null;
}

export interface TradingRentalEstimation {
  id: string;
  estimation_number: string;
  estimation_date: string;
  estimation_type: TradingRentalEstimationType;
  status: string;
  approval_status: string;
  approval_instance_id?: string | null;
  opportunity_id?: string | null;
  customer_id?: string | null;
  customer_name: string;
  contact_person?: string | null;
  project_name?: string | null;
  project_id?: string | null;
  project?: { id: string; project_number: string; project_name: string } | null;
  sales_person_id?: string | null;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  currency: string;
  price_validity_days: number;
  expected_start_date?: string | null;
  remarks?: string | null;
  internal_notes?: string | null;
  terms_and_conditions?: string | null;
  total_cost: number;
  total_overhead?: number;
  material_cost?: number;
  rental_cost?: number;
  loaded_total_cost?: number;
  gross_profit?: number;
  gross_profit_percentage?: number;
  total_margin: number;
  total_before_tax: number;
  total_discount: number;
  total_tax: number;
  grand_total: number;
  related_quotation_count: number;
  created_at: string;
  updated_at: string;
  lines?: TradingRentalEstimationLine[];
  overheads?: TradingRentalEstimationOverhead[];
  overhead_allocations?: TradingRentalEstimationOverheadAllocation[];
}

export interface TradingRentalEstimationRequest {
  estimation_date?: string;
  estimation_type?: TradingRentalEstimationType;
  status?: string;
  approval_status?: string;
  approval_instance_id?: string | null;
  opportunity_id?: string | null;
  customer_id?: string | null;
  customer_name?: string;
  contact_person?: string | null;
  project_name?: string | null;
  project_id?: string | null;
  project?: { id: string; project_number: string; project_name: string } | null;
  sales_person_id?: string | null;
  currency?: string;
  price_validity_days?: number;
  expected_start_date?: string | null;
  remarks?: string | null;
  internal_notes?: string | null;
  terms_and_conditions?: string | null;
  lines?: Array<Partial<TradingRentalEstimationLine>>;
  overheads?: Array<Partial<TradingRentalEstimationOverhead>>;
}

function unwrap<T>(res: unknown): T {
  const d = (res as { data?: { data?: unknown } })?.data?.data ?? (res as { data?: unknown })?.data ?? res;
  return d as T;
}

/** Normalize GET detail/list payloads (handles single- or double-wrapped API envelopes). */
export function normalizeTradingRentalEstimation(raw: unknown): TradingRentalEstimation | null {
  if (raw == null || typeof raw !== 'object') return null;
  let body: Record<string, unknown> = raw as Record<string, unknown>;
  if (body.success === true && body.data != null && typeof body.data === 'object') {
    body = body.data as Record<string, unknown>;
  }
  if (
    body.data != null &&
    typeof body.data === 'object' &&
    !('estimation_number' in body) &&
    !('customer_name' in body)
  ) {
    body = body.data as Record<string, unknown>;
  }
  if (!body.id && !body.estimation_number) return null;
  const lines = Array.isArray(body.lines) ? (body.lines as TradingRentalEstimationLine[]) : [];
  const overheads = Array.isArray(body.overheads) ? (body.overheads as TradingRentalEstimationOverhead[]) : [];
  const overhead_allocations = Array.isArray(body.overhead_allocations)
    ? (body.overhead_allocations as TradingRentalEstimationOverheadAllocation[])
    : [];
  return { ...(body as unknown as TradingRentalEstimation), lines, overheads, overhead_allocations };
}

export function normalizeSelectedLineIds(selectedLineIds?: string[]): string[] {
  if (!Array.isArray(selectedLineIds)) return [];
  return Array.from(new Set(selectedLineIds.map((x) => String(x || '').trim()).filter(Boolean)));
}

class TradingRentalEstimationService {
  private baseURL = '/sales/trading-rental-estimations';

  async getAll(filters?: Record<string, unknown>): Promise<TradingRentalEstimation[]> {
    const res = await api.get(this.baseURL, { params: filters });
    return unwrap<TradingRentalEstimation[]>(res);
  }

  async getById(id: string): Promise<TradingRentalEstimation> {
    const res = await api.get(`${this.baseURL}/${id}`);
    return unwrap<TradingRentalEstimation>(res);
  }

  async create(data: TradingRentalEstimationRequest): Promise<TradingRentalEstimation> {
    const res = await api.post(this.baseURL, data);
    return unwrap<TradingRentalEstimation>(res);
  }

  async update(id: string, data: TradingRentalEstimationRequest): Promise<TradingRentalEstimation> {
    const res = await api.put(`${this.baseURL}/${id}`, data);
    return unwrap<TradingRentalEstimation>(res);
  }

  async remove(id: string): Promise<void> {
    await api.delete(`${this.baseURL}/${id}`);
  }

  async cancel(id: string): Promise<TradingRentalEstimation> {
    const res = await api.post(`${this.baseURL}/${id}/cancel`, {});
    return unwrap<TradingRentalEstimation>(res);
  }

  async convertToQuotation(id: string, selectedLineIds?: string[], departmentId?: string) {
    const normalizedIds = normalizeSelectedLineIds(selectedLineIds);
    const payload: { selectedLineIds?: string[]; department_id?: string } = {};
    if (normalizedIds.length > 0) payload.selectedLineIds = normalizedIds;
    if (departmentId?.trim()) payload.department_id = departmentId.trim();
    const res = await api.post(`${this.baseURL}/${id}/convert-to-quotation`, payload);
    return unwrap<any>(res);
  }

  async getRelatedQuotations(id: string): Promise<any[]> {
    const res = await api.get(`${this.baseURL}/${id}/related-quotations`);
    return unwrap<any[]>(res);
  }

  async syncOpportunityDocuments(id: string): Promise<{ copied: number }> {
    const res = await api.post(`${this.baseURL}/${id}/sync-opportunity-documents`, {});
    const payload = unwrap<{ copied?: number }>(res);
    const n = Number(payload?.copied);
    return { copied: Number.isFinite(n) ? n : 0 };
  }

  async getAvailabilitySummary(id: string): Promise<Record<string, unknown>> {
    const res = await api.get(`${this.baseURL}/${id}/availability-summary`);
    return unwrap<Record<string, unknown>>(res);
  }

  async submitForUniversalApproval(id: string): Promise<TradingRentalEstimation> {
    const res = await api.post(`${this.baseURL}/${id}/submit-for-universal-approval`, {});
    return unwrap<TradingRentalEstimation>(res);
  }

  async approve(id: string, remarks?: string): Promise<TradingRentalEstimation> {
    const res = await api.post(`${this.baseURL}/${id}/approve`, { remarks });
    return unwrap<TradingRentalEstimation>(res);
  }

  async rejectUniversalApproval(id: string, payload: Record<string, unknown>): Promise<TradingRentalEstimation> {
    const res = await api.post(`${this.baseURL}/${id}/reject-universal-approval`, payload);
    return unwrap<TradingRentalEstimation>(res);
  }

  async getOverheadAudit(id: string): Promise<unknown[]> {
    const res = await api.get(`${this.baseURL}/${id}/overhead-audit`);
    return unwrap<unknown[]>(res);
  }

  async downloadLinesImportTemplate(lineType: TradingRentalLineType): Promise<void> {
    const res = await api.getClient().get(`${this.baseURL}/lines-import/template`, {
      params: { type: lineType.toLowerCase() },
      responseType: 'blob',
    });
    triggerBlobDownload(
      new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `TRE_${lineType}_Lines_Template.xlsx`
    );
  }

  /** Validate + calculate a Trading or Rental line file without saving. */
  async previewImportLines(file: File, lineType: TradingRentalLineType): Promise<TradingRentalLineImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('line_type', lineType);
    const res = await api.getClient().post(`${this.baseURL}/lines-import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<TradingRentalLineImportPreview>(res);
  }

  async importLines(
    id: string,
    file: File,
    lineType: TradingRentalLineType,
    mode: TradingRentalLineImportMode
  ): Promise<{ estimation: TradingRentalEstimation; imported: number; warnings: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('line_type', lineType);
    formData.append('mode', mode);
    const res = await api.getClient().post(`${this.baseURL}/${id}/import-lines`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const body = unwrap<{ estimation: TradingRentalEstimation; imported: number; warnings: string[] }>(res);
    const normalized = normalizeTradingRentalEstimation(body.estimation);
    return {
      ...body,
      estimation: normalized || (body.estimation as TradingRentalEstimation),
    };
  }
}

export default new TradingRentalEstimationService();

